/**
 * Script Component Generator — Lightweight Component Suggestion
 *
 * Unlike the full agent component generator (generateAgentComponents), this:
 * - Reuses the script's proven query rows via queryId — writes NO new SQL
 * - Binds each component to ONE source (the script already did any cross-source
 *   work in JS and returned a combined dataset — no federation at render time)
 * - Uses a simpler prompt — LLM picks component types and maps columns
 * - Faithfully represents the script's output; never re-derives or invents data
 *
 * Falls back to generateAgentComponents if this fails.
 */

import { LLM } from '../../llm';
import { promptLoader } from '../prompt-loader';
import { queryCache } from '../../utils/query-cache';
import { logger } from '../../utils/logger';
import type { Component } from '../../types';
import type { Action } from '../../threads/action';
import type { ScriptResult, ScriptComponentSpec } from './types';
import { TOOL_TRACKING_SAMPLE_ROWS, MAX_ROWS_RENDERED } from '../constants';
import type { ExternalTool } from '../services/tool-executor-service';

export interface ScriptComponentResult {
	components: Component[];
	layoutTitle: string;
	layoutDescription: string;
	actions: Action[];
	/**
	 * Reusable, source-bound specs (no ephemeral queryId) for each component
	 * that can be deterministically replayed. Persisted on the recipe so a
	 * tier-high replay can rebind them to fresh queryIds without the LLM.
	 * Components that can't be replayed cleanly (e.g. federation) are omitted.
	 */
	componentSpecs: ScriptComponentSpec[];
}

// ============================================
// Deterministic component validation + repair
// ============================================

type ColType = 'NUMBER' | 'TEXT' | 'DATE' | 'BOOLEAN';
interface ColInfo {
	name: string;
	type: ColType;
	/** True when every sampled non-null value is identical — a per-row invariant
	 * (e.g. a "total" the script computed the same on every row). Such a column
	 * must NOT be summed for a KPI — its single value IS the answer. */
	invariant: boolean;
}

/** Infer column name + type + invariance from a sample of rows. */
function inferColumns(rows: any[]): Map<string, ColInfo> {
	const cols = new Map<string, ColInfo>();
	if (!Array.isArray(rows) || rows.length === 0) return cols;
	const sample = rows.slice(0, 20);
	const keys = new Set<string>();
	for (const r of sample) {
		if (r && typeof r === 'object') for (const k of Object.keys(r)) keys.add(k);
	}
	for (const key of keys) {
		let type: ColType = 'TEXT';
		let typed = false;
		const distinct = new Set<string>();
		for (const r of sample) {
			const v = r?.[key];
			if (v === null || v === undefined) continue;
			if (!typed) {
				if (typeof v === 'number') type = 'NUMBER';
				else if (typeof v === 'boolean') type = 'BOOLEAN';
				else if (v instanceof Date) type = 'DATE';
				else if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) type = 'NUMBER';
				else type = 'TEXT';
				typed = true;
			}
			distinct.add(v instanceof Date ? v.toISOString() : String(v));
		}
		cols.set(key.toLowerCase(), { name: key, type, invariant: distinct.size <= 1 });
	}
	return cols;
}

function resolveCol(cols: Map<string, ColInfo>, key: any): ColInfo | undefined {
	if (key == null) return undefined;
	return cols.get(String(key).toLowerCase());
}

function firstOfType(cols: Map<string, ColInfo>, type: ColType): ColInfo | undefined {
	for (const c of cols.values()) if (c.type === type) return c;
	return undefined;
}

const ALL_KEY_NAMES = ['xAxisKey', 'yAxisKey', 'valueKey', 'nameKey', 'seriesKey', 'groupBy', 'aggregationField', 'categoryKey'];
const AVG_KEYWORDS = /\b(average|avg|mean)\b/i;
const COUNT_KEYWORDS = /\b(count|number of|how many|# of|no\.? of)\b/i;
const SUM_KEYWORDS = /\b(total|sum|overall|combined|aggregate|all)\b/i;

/**
 * Deterministically validate + repair one component's config against the real
 * columns of its bound data source. Rewrites every config key to the EXACT
 * column name (case-insensitive match), drops keys that reference non-existent
 * columns, enforces TEXT/NUMBER axis roles (with swap), and forces aggregation
 * on multi-row "total/average/count" KPI cards — otherwise the card silently
 * renders data[0]. Returns the repaired component, or null when it cannot be
 * rendered from the available columns (caller drops it).
 *
 * Frontends are already case-insensitive, but we canonicalize here so the
 * stored recipe (Phase 3) is correct and type-role fixes are baked in rather
 * than left to per-component runtime heuristics.
 */
function validateAndRepairComponent(
	comp: any,
	componentType: string,
	cols: Map<string, ColInfo>,
	rowCount: number,
): any | null {
	if (cols.size === 0) return comp; // no columns to validate against — leave untouched
	const props = comp.props || (comp.props = {});
	const cfg = props.config || (props.config = {});

	// Generic pass — rewrite every present key to its exact column name; drop unknowns.
	for (const k of ALL_KEY_NAMES) {
		if (cfg[k] == null) continue;
		const r = resolveCol(cols, cfg[k]);
		if (r) cfg[k] = r.name; else delete cfg[k];
	}
	if (Array.isArray(cfg.metricKeys)) {
		cfg.metricKeys = cfg.metricKeys.map((m: any) => resolveCol(cols, m)?.name).filter(Boolean);
		if (cfg.metricKeys.length === 0) delete cfg.metricKeys;
	}

	const numeric = () => firstOfType(cols, 'NUMBER');
	const category = () => firstOfType(cols, 'TEXT') || firstOfType(cols, 'DATE');
	const hasMetrics = Array.isArray(cfg.metricKeys) && cfg.metricKeys.length > 0;

	switch (componentType) {
		case 'KPICard':
		case 'GaugeChart': {
			const vk = resolveCol(cols, cfg.valueKey) || numeric() || category();
			if (!vk) return null;
			cfg.valueKey = vk.name;
			const title = String(props.title || '');
			// Multi-row KPI with an aggregate-sounding title must aggregate, else it
			// silently shows data[0]. BUT only sum/avg a column that actually VARIES
			// across rows — an invariant column (e.g. a per-device "total" the script
			// computed identically on every row) should display its value as-is, not
			// be summed. `count` ignores invariance (it counts rows).
			if (rowCount > 1 && !cfg.aggregation) {
				if (COUNT_KEYWORDS.test(title)) cfg.aggregation = 'count';
				else if (!vk.invariant) {
					if (AVG_KEYWORDS.test(title)) cfg.aggregation = 'avg';
					else if (SUM_KEYWORDS.test(title)) cfg.aggregation = 'sum';
				}
			}
			if (cfg.aggregation === 'sum' || cfg.aggregation === 'avg') {
				const af = resolveCol(cols, cfg.aggregationField);
				if (af && af.type === 'NUMBER') {
					cfg.aggregationField = af.name;
				} else {
					const n = vk.type === 'NUMBER' ? vk : numeric();
					if (n) cfg.aggregationField = n.name;
					else { delete cfg.aggregation; delete cfg.aggregationField; }
				}
			}
			return comp;
		}
		case 'PieChart':
		case 'TreemapChart': {
			let nameC = resolveCol(cols, cfg.nameKey);
			let valC = resolveCol(cols, cfg.valueKey);
			if (nameC?.type === 'NUMBER' && valC && valC.type !== 'NUMBER') { const t = nameC; nameC = valC; valC = t; }
			if (!nameC) nameC = category();
			if (!valC || valC.type !== 'NUMBER') valC = numeric() || valC;
			if (!nameC || !valC) return null;
			cfg.nameKey = nameC.name;
			cfg.valueKey = valC.name;
			return comp;
		}
		case 'WaterfallChart': {
			const catC = resolveCol(cols, cfg.categoryKey) || category();
			let valC = resolveCol(cols, cfg.valueKey);
			if (!valC || valC.type !== 'NUMBER') valC = numeric() || valC;
			if (!catC || !valC) return null;
			cfg.categoryKey = catC.name;
			cfg.valueKey = valC.name;
			return comp;
		}
		case 'ScatterChart': {
			// Scatter plots numeric vs numeric.
			let xC = resolveCol(cols, cfg.xAxisKey);
			let yC = resolveCol(cols, cfg.yAxisKey);
			if (!xC || xC.type !== 'NUMBER') xC = numeric() || xC;
			if (!yC || yC.type !== 'NUMBER') {
				// pick a numeric column distinct from x if possible
				yC = [...cols.values()].find(c => c.type === 'NUMBER' && c.name !== xC?.name) || numeric() || yC;
			}
			if (!xC || !yC) return null;
			cfg.xAxisKey = xC.name;
			cfg.yAxisKey = yC.name;
			return comp;
		}
		case 'HeatmapChart': {
			// x = category, y = category, value = numeric.
			const xC = resolveCol(cols, cfg.xAxisKey) || category();
			const yC = resolveCol(cols, cfg.yAxisKey) || [...cols.values()].find(c => (c.type === 'TEXT' || c.type === 'DATE') && c.name !== xC?.name);
			let valC = resolveCol(cols, cfg.valueKey);
			if (!valC || valC.type !== 'NUMBER') valC = numeric() || valC;
			if (!xC || !yC || !valC) return null;
			cfg.xAxisKey = xC.name;
			cfg.yAxisKey = yC.name;
			cfg.valueKey = valC.name;
			return comp;
		}
		case 'BarChart':
		case 'LineChart': {
			let xC = resolveCol(cols, cfg.xAxisKey);
			let yC = resolveCol(cols, cfg.yAxisKey);
			// y must be numeric. If y is text and x is numeric, the LLM swapped them.
			if (!hasMetrics && xC?.type === 'NUMBER' && yC && yC.type !== 'NUMBER') { const t = xC; xC = yC; yC = t; }
			// LineChart is a time series — prefer a DATE column for the x-axis.
			if (!xC) xC = (componentType === 'LineChart' ? (firstOfType(cols, 'DATE') || category()) : category()) || firstOfType(cols, 'NUMBER');
			if (!xC) return null;
			cfg.xAxisKey = xC.name;
			if (!hasMetrics) {
				if (!yC || yC.type !== 'NUMBER') yC = numeric();
				if (!yC) return null;
				cfg.yAxisKey = yC.name;
			}
			// seriesKey (multi-line) must be a TEXT column distinct from x — else it
			// produces a broken/overlapping chart. Drop it if invalid; the generic
			// pass above already resolved it to an exact column name (or dropped it).
			if (cfg.seriesKey) {
				const sC = resolveCol(cols, cfg.seriesKey);
				if (!sC || sC.type !== 'TEXT' || sC.name === cfg.xAxisKey) delete cfg.seriesKey;
				else cfg.seriesKey = sC.name;
			}
			return comp;
		}
		case 'RadarChart': {
			if (!hasMetrics) {
				const vk = resolveCol(cols, cfg.valueKey) || numeric();
				if (!vk) return null;
				cfg.valueKey = vk.name;
			}
			return comp;
		}
		default:
			// DataTable, MapView, MarkdownBlock, etc. — generic key fixing above is sufficient.
			return comp;
	}
}

/**
 * Generate components using the lightweight script-components prompt.
 * Each component binds to one of the script's sources by sourceIndex.
 */
/** Shared context for building a single script component (loop + early streamer). */
interface ScriptCompCtx {
	queries: any[];
	queryIds: Record<string, string>;
	columnsByIndex: Map<string, ColInfo>[];
	availableComponents: Component[];
}

/** Stable identity used to dedup the early-streamed answer against the main loop. */
function scriptCompKey(comp: any): string {
	const id = comp?.componentId || comp?.componentName || '';
	const src = comp?.sourceIndex ?? '';
	return `${id}|${src}|${JSON.stringify(comp?.props?.config ?? comp?.config ?? {})}`;
}

/**
 * Extract the `answerComponent` object from a partial/streaming LLM response,
 * once `hasAnswerComponent: true` has appeared. Brace-matched JSON slice — same
 * technique as the agent generator's tryStreamAnswerComponent. Returns null
 * until the object is complete/parseable.
 */
function extractAnswerComponentObject(text: string): any | null {
	const hasMatch = text.match(/"hasAnswerComponent"\s*:\s*(true|false)/);
	if (!hasMatch || hasMatch[1] !== 'true') return null;
	const startMatch = text.match(/"answerComponent"\s*:\s*\{/);
	if (!startMatch) return null;
	const startPos = startMatch.index! + startMatch[0].length - 1;
	let depth = 0, inString = false, escapeNext = false, endPos = -1;
	for (let i = startPos; i < text.length; i++) {
		const c = text[i];
		if (escapeNext) { escapeNext = false; continue; }
		if (c === '\\') { escapeNext = true; continue; }
		if (c === '"') { inString = !inString; continue; }
		if (!inString) {
			if (c === '{') depth++;
			else if (c === '}') { depth--; if (depth === 0) { endPos = i + 1; break; } }
		}
	}
	if (endPos <= startPos) return null;
	try { return JSON.parse(text.substring(startPos, endPos)); } catch { return null; }
}

/**
 * Build one Component from an LLM component spec: resolve the template, bind the
 * source's queryId, and validate/repair the config against the real columns.
 * Returns null when it can't be built (unknown template, missing queryId, failed
 * validation). Shared by the early answer-streamer and the main loop so the two
 * never drift.
 */
async function buildScriptComponent(
	comp: any,
	ctx: ScriptCompCtx,
): Promise<{ component: Component; spec?: ScriptComponentSpec } | null> {
	const { queries, queryIds, columnsByIndex, availableComponents } = ctx;

	let validationCols: Map<string, ColInfo> = new Map();
	let validationRowCount = 0;
	let specSourceRef = '';

	// Be permissive — the LLM may use componentId or componentName, id or name.
	const wantedId = comp.componentId || '';
	const wantedName = comp.componentName || '';
	const template = availableComponents.find(c =>
		c.id === wantedId || c.name === wantedId || c.id === wantedName || c.name === wantedName,
	);
	if (!template) {
		logger.warn(`[ScriptComponentGen] Component "${wantedId || wantedName}" not found — skipping`);
		return null;
	}

	// Markdown blocks have no data source.
	if (template.type === 'MarkdownBlock') {
		const p: any = comp.props || {};
		// DynamicMarkdownBlock reads props.content. The LLM often nests the text
		// under config.content (mirroring the chart shape), which makes the
		// component fall back to its demo DEFAULT_CONTENT ("Overview / Revenue
		// grew 15% ..."). Lift the real text up to props.content.
		const content = p.content ?? p.config?.content ?? p.config?.markdown ?? p.config?.text ?? '';
		return {
			component: {
				id: `comp_${Math.random().toString(36).substring(2, 8)}`,
				name: template.name,
				displayName: template.displayName,
				type: template.type,
				description: template.description,
				props: { title: p.title, description: p.description, content },
				category: template.category,
				keywords: template.keywords,
			},
			// Persist a content-bearing spec so the markdown survives deterministic
			// replay (it has no data source, so its text must be stored — otherwise
			// re-running a matched script rebuilds only the source-bound components
			// and silently drops the narrative).
			spec: {
				componentType: template.name,
				sourceRef: 'markdown',
				title: p.title,
				description: p.description,
				content,
				config: {},
			},
		};
	}

	// Every script component binds to one source's pre-executed rows via queryId.
	// (No federation: the script already did any cross-source work in JS and
	// returned a single combined dataset — the component just renders it.)
	const sourceIdx = typeof comp.sourceIndex === 'number' ? comp.sourceIndex : 0;
	const targetQuery = queries[sourceIdx] || queries[0];
	const targetQueryId = queryIds[targetQuery.sourceId];
	if (!targetQueryId) {
		logger.warn(`[ScriptComponentGen] No queryId for source "${targetQuery.sourceName}" — skipping`);
		return null;
	}
	validationCols = columnsByIndex[sourceIdx] || inferColumns(targetQuery.data);
	validationRowCount = targetQuery.count ?? (targetQuery.data?.length || 0);
	specSourceRef = targetQuery.sourceId;
	// Virtual sources route through the script_dataset sentinel toolId.
	const renderedToolId = targetQuery.virtual ? 'script_dataset' : targetQuery.sourceId;
	const externalToolProp = { toolId: renderedToolId, toolName: targetQuery.sourceName, parameters: { queryId: targetQueryId } };

	const repaired = validateAndRepairComponent(comp, template.type, validationCols, validationRowCount);
	if (!repaired) {
		logger.warn(`[ScriptComponentGen] Component "${template.name}" dropped — config keys could not be mapped to the data columns`);
		return null;
	}

	// DataTable columns: DynamicDataTable expects [{key,label}] objects. The LLM
	// sometimes emits plain strings, which the table can't map and so renders its
	// demo defaults. Normalize string columns to {key,label}.
	if (template.type === 'DataTable' && Array.isArray(comp.props?.config?.columns)) {
		comp.props.config.columns = comp.props.config.columns.map((c: any) =>
			typeof c === 'string'
				? { key: c, label: c.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m: string) => m.toUpperCase()) }
				: c,
		);
	}

	const component: Component = {
		id: `comp_${Math.random().toString(36).substring(2, 8)}`,
		name: template.name,
		displayName: template.displayName,
		type: template.type,
		description: template.description,
		props: { ...comp.props, externalTool: externalToolProp },
		category: template.category,
		keywords: template.keywords,
	};

	// Reusable, queryId-free spec for deterministic replay (skipped for federation).
	const spec: ScriptComponentSpec | undefined = specSourceRef
		? {
			componentType: template.name,
			sourceRef: specSourceRef,
			title: comp.props?.title,
			description: comp.props?.description,
			config: { ...(comp.props?.config || {}) },
		}
		: undefined;

	return { component, spec };
}

export async function generateScriptComponents(params: {
	userPrompt: string;
	scriptResult: ScriptResult;
	queryIds: Record<string, string>;
	availableComponents: Component[];
	apiKey?: string;
	model?: string;
	componentStreamCallback?: (component: Component) => void;
	/** Accepted for call-site compatibility; no longer used (federation removed). */
	externalTools?: ExternalTool[];
	/** Accepted for call-site compatibility; no longer used (federation removed). */
	collections?: any;
	/** Cleaned analysis/narrative text — used to inject a fallback DynamicMarkdownBlock if the LLM omits one (markdown is required in every response). */
	analysisText?: string;
	/** Per-turn cancellation — aborts the component-generation LLM call if the user hit Stop. */
	signal?: AbortSignal;
}): Promise<ScriptComponentResult> {
	// `externalTools`/`collections` remain on the params type for call-site compat
	// but are no longer used here (federation was removed from the script flow).
	const { userPrompt, scriptResult, queryIds, availableComponents, apiKey, model, componentStreamCallback, analysisText, signal } = params;

	const queries = scriptResult.executedQueries;
	if (!queries || queries.length === 0) {
		logger.warn(`[ScriptComponentGen] No query data available — returning empty`);
		return { components: [], layoutTitle: '', layoutDescription: '', actions: [], componentSpecs: [] };
	}

	logger.info(`[ScriptComponentGen] Starting | ${queries.length} queries | ${availableComponents.length} available components`);

	// Pre-infer columns+types per source for deterministic component validation.
	// Virtual (computed:_final / script_dataset) sources may arrive without inline
	// rows when the saved script's executedQueries were reconstructed — fall back to
	// the script's final dataset so chart configs are still validated (and dangling
	// keys like a non-existent seriesKey get stripped instead of breaking the chart).
	const columnsByIndex = queries.map(q => {
		const data = (q.data && q.data.length) ? q.data : (q.virtual ? scriptResult.data : q.data);
		return inferColumns(data || []);
	});

	// Build available components description. Include each component's full Props
	// Structure (the same rich metadata the agent/text flow exposes) — not just
	// name+description — so the LLM sees every valid prop and fills them in. This
	// is what restores axis labels (xAxisLabel/yAxisLabel) and other config props
	// the slimmed name-only listing had silently dropped.
	const availableCompsText = availableComponents
		.map((c, idx) => {
			const keywords = c.keywords ? c.keywords.join(', ') : '';
			const propsPreview = c.props ? JSON.stringify(c.props, null, 2) : 'No props';
			return `${idx + 1}. ID: ${c.id}
   Name: ${c.name}
   Type: ${c.type}
   Description: ${c.description || 'No description'}
   Keywords: ${keywords}
   Props Structure: ${propsPreview}`;
		})
		.join('\n\n');

	// Build data description for ALL sources
	const sourceDescriptions = queries.map((q, idx) => {
		if (!q.data || q.data.length === 0) return null;

		// Use the SAME inferred column types validateAndRepairComponent uses, so the
		// LLM sees NUMBER for numeric columns even when DuckDB returns ints/counts as
		// strings (e.g. "232"). The naive first-row typeof mislabeled those as TEXT,
		// which pushed numeric columns onto the wrong axis. Fall back to bare keys if
		// inference is empty.
		const cols = columnsByIndex[idx];
		const columns = (cols && cols.size > 0)
			? Array.from(cols.values()).map(c => `${c.name} (${c.type})`).join(', ')
			: Object.keys(q.data[0]).join(', ');

		const sampleData = JSON.stringify(q.data.slice(0, TOOL_TRACKING_SAMPLE_ROWS), null, 2);

		// The component renders at most MAX_ROWS_RENDERED rows (the queryId payload is
		// capped). State the FACT only — how many render vs the full universe — in the
		// exact "X (rendered) of Y total" shape the prompt's render-cap rule keys off.
		// The instruction lives in the prompt, not here (keep data lines factual).
		const universe = Math.max(q.count, q.totalCount || 0);
		const rendered = Math.min(q.count, MAX_ROWS_RENDERED);
		const rowsLine = rendered < universe ? `${rendered} of ${universe} (only first ${rendered} shown)` : `${universe}`;

		return `### Source ${idx}: "${q.sourceName}" (sourceIndex: ${idx})
- Rows: ${rowsLine}
- Columns: ${columns}
- Sample data:
\`\`\`json
${sampleData}
\`\`\``;
	}).filter(Boolean).join('\n\n');

	try {
		const prompts = await promptLoader.loadPrompts('script-components', {
			USER_PROMPT: userPrompt,
			AVAILABLE_COMPONENTS: availableCompsText,
			SOURCE_NAME: queries.map(q => q.sourceName).join(', '),
			ROW_COUNT: queries.map(q => `${q.sourceName}: ${Math.min(q.count, MAX_ROWS_RENDERED)}${q.count > MAX_ROWS_RENDERED ? ` of ${q.count}` : ''}`).join(', '),
			COLUMN_DESCRIPTIONS: sourceDescriptions,
			SAMPLE_DATA: '',
			ANALYSIS_TEXT: (analysisText && analysisText.trim())
				? analysisText.trim()
				: 'No analysis text provided — infer intent from the user question and the returned data.',
		});

		// Shared build context for the early answer-streamer and the loop.
		const ctx: ScriptCompCtx = {
			queries, queryIds, columnsByIndex, availableComponents,
		};

		// Answer-component early streaming (parity with the agent flow): as the LLM
		// streams, detect `answerComponent` and emit it to the UI first, before the
		// rest of the dashboard. The main loop then skips re-streaming it.
		let fullText = '';
		let answerAttempted = false;
		let streamedAnswerKey: string | null = null;
		const partial = componentStreamCallback ? (chunk: string) => {
			fullText += chunk;
			if (answerAttempted) return;
			const answerComp = extractAnswerComponentObject(fullText);
			if (!answerComp) return;
			answerAttempted = true; // sync guard — attempt the early stream only once
			streamedAnswerKey = scriptCompKey(answerComp);
			buildScriptComponent(answerComp, ctx)
				.then(built => {
					if (built) {
						componentStreamCallback(built.component);
						logger.info('[ScriptComponentGen] Streamed answer component early');
					} else {
						streamedAnswerKey = null; // build failed — let the loop produce it
					}
				})
				.catch(() => { streamedAnswerKey = null; });
		} : undefined;

		// Log the component-step prompt (parity with mainAgent/sourceAgent/etc.) so the
		// script-flow component generation — including the chart title/description it
		// produces — is visible in superatom-sdk.log for debugging.
		logger.logLLMPrompt('scriptComponentGen', 'system', prompts.system);
		logger.logLLMPrompt('scriptComponentGen', 'user', prompts.user);

		const response = await LLM.stream<any>(
			{ sys: prompts.system, user: prompts.user },
			{
				model: model || undefined,
				maxTokens: 5000,
				temperature: 0,
				apiKey,
				partial,
				signal,
			},
			true,
		);

		// Log the RAW LLM output so we can see exactly which components it suggested
		// (and whether it included a DynamicMarkdownBlock or omitted one → fallback).
		logger.logLLMPrompt('scriptComponentGen:response', 'user', JSON.stringify(response, null, 2));

		if (!response || !response.components || !Array.isArray(response.components)) {
			logger.warn(`[ScriptComponentGen] LLM returned invalid response`);
			return { components: [], layoutTitle: '', layoutDescription: '', actions: [], componentSpecs: [] };
		}

		const layoutTitle = response.layoutTitle || 'Dashboard';
		const layoutDescription = response.layoutDescription || '';
		const matchedComponents: Component[] = [];
		const componentSpecs: ScriptComponentSpec[] = [];

		// Identity of the designated answer component. Parity with the agent flow
		// (matchComponentsFromAnalysis), which streams ONLY the answer component —
		// never the rest of the dashboard.
		const answerKey = response.answerComponent ? scriptCompKey(response.answerComponent) : null;

		for (const comp of response.components) {
			const built = await buildScriptComponent(comp, ctx);
			if (!built) continue;
			matchedComponents.push(built.component);
			if (built.spec) componentSpecs.push(built.spec);
			// Stream ONLY the answer component, and only if the early partial-stream
			// didn't already emit it. The OTHER components must NOT be streamed as
			// answer markers: on the frontend each marker REPLACES the inline answer
			// (schema.ui), so streaming them all left only the last one visible and
			// hid the rest. The full set is delivered in the final dashboard container.
			if (componentStreamCallback && answerKey && scriptCompKey(comp) === answerKey && streamedAnswerKey !== answerKey) {
				componentStreamCallback(built.component);
				streamedAnswerKey = answerKey;
			}
		}

		// Safety: the LLM declared an answer component but didn't list it in
		// `components` — build + prepend it so the primary answer is always present.
		if (response.hasAnswerComponent && response.answerComponent) {
			const aKey = scriptCompKey(response.answerComponent);
			const alreadyIncluded = response.components.some((c: any) => scriptCompKey(c) === aKey);
			if (!alreadyIncluded) {
				const built = await buildScriptComponent(response.answerComponent, ctx);
				if (built) {
					matchedComponents.unshift(built.component);
					if (built.spec) componentSpecs.unshift(built.spec);
					if (componentStreamCallback && aKey !== streamedAnswerKey) componentStreamCallback(built.component);
				}
			}
		}

		// Guarantee a DynamicMarkdownBlock in EVERY response. The prompt asks for one,
		// but enforce it so the UI always carries the narrative summary even when the
		// LLM omits it. Uses the caller's cleaned analysis text as the content.
		const hasMarkdown = matchedComponents.some(c => c.type === 'MarkdownBlock');
		if (!hasMarkdown && analysisText && analysisText.trim()) {
			const mdTemplate = availableComponents.find(c => c.type === 'MarkdownBlock' || c.name === 'DynamicMarkdownBlock');
			if (mdTemplate) {
				const mdComponent: Component = {
					id: `comp_${Math.random().toString(36).substring(2, 8)}`,
					name: mdTemplate.name,
					displayName: mdTemplate.displayName,
					type: mdTemplate.type,
					description: mdTemplate.description,
					props: { content: analysisText.trim() },
					category: mdTemplate.category,
					keywords: mdTemplate.keywords,
				};
				matchedComponents.push(mdComponent);
				// Persist it too, so deterministic replay keeps the narrative.
				componentSpecs.push({
					componentType: mdTemplate.name,
					sourceRef: 'markdown',
					content: analysisText.trim(),
					config: {},
				});
				if (componentStreamCallback) componentStreamCallback(mdComponent);
				logger.info('[ScriptComponentGen] Injected fallback DynamicMarkdownBlock (LLM omitted one)');
			}
		}

		logger.info(`[ScriptComponentGen] Generated ${matchedComponents.length} components`);

		const actions: Action[] = (response.actions || []).map((a: any, idx: number) => ({
			id: `action_${idx}_${Date.now()}`,
			name: a.name || a.question || '',
			type: 'next_question' as const,
			question: a.question || a.name || '',
		}));

		return {
			components: matchedComponents,
			layoutTitle,
			layoutDescription,
			actions,
			componentSpecs,
		};

	} catch (error) {
		const msg = error instanceof Error ? error.message : String(error);
		logger.error(`[ScriptComponentGen] Failed: ${msg}`);
		return { components: [], layoutTitle: '', layoutDescription: '', actions: [], componentSpecs: [] };
	}
}

/**
 * Generate the DynamicMarkdownBlock content with ONE small LLM call: condense this
 * run's analysis into 2-4 concise bullets. Used on deterministic replay, where the
 * data components are rebuilt without an LLM but the markdown still needs a real,
 * fresh summary (the stored content has authoring-time numbers; the raw analysis is
 * too long). Returns '' on any failure so the caller can fall back.
 */
async function summarizeToMarkdown(analysis: string, apiKey?: string, model?: string): Promise<string> {
	if (!analysis || !analysis.trim()) return '';
	try {
		// Editable at backend/.prompts/script-component-summary/ (falls back to the
		// hardcoded default in prompts.ts when no override file is present).
		const prompts = await promptLoader.loadPrompts('script-component-summary', { ANALYSIS_TEXT: analysis });
		const text = await LLM.text({ sys: prompts.system, user: prompts.user }, { model: model || undefined, maxTokens: 400, temperature: 0, apiKey });
		return (text || '').trim();
	} catch (err) {
		logger.warn(`[ScriptComponentGen] summarizeToMarkdown failed, falling back: ${err instanceof Error ? err.message : String(err)}`);
		return '';
	}
}

/**
 * Deterministically rebuild components from previously-validated specs (the
 * component recipe), binding each to a FRESH queryId from this run's data.
 * No LLM call. Returns null when the specs can't be cleanly replayed (missing
 * source, unknown component, federation spec, or column drift) — the caller
 * should then fall back to `generateScriptComponents`.
 *
 * See backend/docs/SCRIPT-COMPONENT-CONSISTENCY.md (Phase 3).
 */
export async function assembleComponentsFromSpecs(params: {
	specs: ScriptComponentSpec[];
	scriptResult: ScriptResult;
	queryIds: Record<string, string>;
	availableComponents: Component[];
	componentStreamCallback?: (component: Component) => void;
	/** This run's fresh analysis — summarized by an LLM into the markdown block. */
	analysisText?: string;
	/** Passed through to the markdown summarize LLM call. */
	apiKey?: string;
	model?: string;
}): Promise<Component[] | null> {
	const { specs, scriptResult, queryIds, availableComponents, componentStreamCallback, analysisText, apiKey, model } = params;
	if (!specs || specs.length === 0) return null;

	const queries = scriptResult.executedQueries || [];
	const byId = new Map(queries.map(q => [q.sourceId, q]));
	const out: Component[] = [];
	let answerComponentStreamed = false;

	const mdTemplate = availableComponents.find(c => c.type === 'MarkdownBlock' || c.name === 'DynamicMarkdownBlock');
	const buildMarkdown = (content: string, title?: string, description?: string): Component | null => {
		if (!mdTemplate || !content || !content.trim()) return null;
		return {
			id: `comp_${Math.random().toString(36).substring(2, 8)}`,
			name: mdTemplate.name,
			displayName: mdTemplate.displayName,
			type: mdTemplate.type,
			description: mdTemplate.description,
			props: { title, description, content: content.trim() },
			category: mdTemplate.category,
			keywords: mdTemplate.keywords,
		};
	};

	for (const spec of specs) {
		if (spec.sourceRef === 'federation') {
			logger.info('[ScriptComponentGen] Recipe has a federation spec — not replayable deterministically, falling back to LLM');
			return null;
		}

		// Markdown is content-only (no data source). Generate a FRESH, concise summary
		// from THIS run's analysis via one small LLM call — so the block is an
		// LLM-written 2-4 bullet summary, never the full analysis dump and never the
		// stored authoring-time numbers (which would be stale on a param-changed
		// replay). Fall back to the stored content / raw analysis if the call fails.
		if (spec.sourceRef === 'markdown') {
			const summary = (await summarizeToMarkdown(analysisText || '', apiKey, model)) || spec.content || analysisText || '';
			const md = buildMarkdown(summary, spec.title, spec.description);
			if (md) {
				out.push(md);
				if (componentStreamCallback) componentStreamCallback(md);
			}
			continue;
		}

		const q = byId.get(spec.sourceRef) || queries.find(x => x.virtual);
		if (!q || !q.data || q.data.length === 0) {
			logger.info(`[ScriptComponentGen] Recipe spec source "${spec.sourceRef}" missing/empty on replay — falling back to LLM`);
			return null;
		}

		const template = availableComponents.find(c => c.name === spec.componentType || c.id === spec.componentType);
		if (!template) {
			logger.info(`[ScriptComponentGen] Recipe spec component "${spec.componentType}" not in library — falling back to LLM`);
			return null;
		}

		// Cheap drift check — re-validate the stored config against this run's columns.
		const cols = inferColumns(q.data);
		const comp: any = { props: { title: spec.title, description: spec.description, config: { ...(spec.config || {}) } } };
		const repaired = validateAndRepairComponent(comp, template.type, cols, q.count ?? q.data.length);
		if (!repaired) {
			logger.info(`[ScriptComponentGen] Recipe spec "${spec.componentType}" no longer maps to columns (shape drift) — falling back to LLM`);
			return null;
		}

		const queryId = queryIds[q.sourceId] || queryCache.storeQuery(q.sql, { data: q.data, count: q.count });
		const toolId = q.virtual ? 'script_dataset' : q.sourceId;

		const component: Component = {
			id: `comp_${Math.random().toString(36).substring(2, 8)}`,
			name: template.name,
			displayName: template.displayName,
			type: template.type,
			description: template.description,
			props: {
				...comp.props,
				externalTool: { toolId, toolName: q.sourceName, parameters: { queryId } },
			},
			category: template.category,
			keywords: template.keywords,
		};
		out.push(component);
		if (componentStreamCallback && !answerComponentStreamed) {
			componentStreamCallback(component);
			answerComponentStreamed = true;
		}
	}

	// Guarantee a markdown narrative on EVERY replay — covers older recipes saved
	// before markdown was persisted as a spec. Summarize this run's analysis.
	if (out.length > 0 && !out.some(c => c.type === 'MarkdownBlock')) {
		const summary = (await summarizeToMarkdown(analysisText || '', apiKey, model)) || analysisText || '';
		const md = buildMarkdown(summary);
		if (md) {
			out.push(md);
			if (componentStreamCallback) componentStreamCallback(md);
			logger.info('[ScriptComponentGen] Injected markdown on replay (recipe had no markdown spec)');
		}
	}

	logger.info(`[ScriptComponentGen] Deterministically assembled ${out.length} component(s) from recipe — no LLM call`);
	return out.length > 0 ? out : null;
}
